import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { ALERT_TYPE } from 'src/app/constants';
import { Enums } from 'src/app/Shared/enums';
import { SharedService } from 'src/app/Shared/shared-service.service';
import { Company } from 'src/model/company.model';
import { AdminAccess, CompanyAccess } from 'src/model/companyAccess.model';
import { User } from 'src/model/user.model';
import { AccessType, AdminAccessService } from 'src/services/admin-access.service';
import { CompanyService } from 'src/services/company.service';
import { UserService } from 'src/services/user.service';

export enum AccessAction {
  Add,
  Change,
  Clone,
  Duty,
  RevokeTemp,
  RevokeTransfer
}

/**
 * Add - Grant access of essential priority to new admin. If the admin already has TEMP access of that company, revoke and grant essential priority access
 * Change - Grant essential priority access to new admin, revoke access of previous admin. For TEMP access, revoke and grant essential priority access
 * Clone - Clone essential priority access (100, 200, 300) and TEMP access (101, 201, 301) to new admin as TEMP access. If the admin already has essential or TEMP access of that company, skip. For ON DUTY access, revoke and grant TEMP priority access
 * Duty - Grant access of ON DUTY priority (401) to new admin for all companies. If the admin already has access of that company, skip
 * RevokeTemp - Revoke TEMP access (101, 201, 301, 401) of selected admin
 * RevokeTransfer - Same as Change, but in batch
 */

@Component({
    selector: 'app-access-action',
    templateUrl: './access-action.component.html',
    styleUrl: './access-action.component.scss',
    standalone: false
})
export class AccessActionComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  sharedService = inject(SharedService);
  enums = inject(Enums);
  private adminAccessService = inject(AdminAccessService);
  private userService = inject(UserService);
  private companyService = inject(CompanyService);

  @Input() action: AccessAction;

  @Input() companies: number[];
  @Input() currentAdmin: string;
  @Input() currentAccess: AdminAccess;
  @Input() adminAccessList: AdminAccess[];
  @Input() companyAccessList: CompanyAccess[] = [];
  // @Input() companyAdmin: CompanyAdmins;
  @Input() targetPriority: number;

  // @Input() revokeCompanyIdList: number[];
  @Input() currentOnDutyAdmins: string[];

  @Output() shouldReload = new EventEmitter<any>();

  companyAccessMap = new Map<string, CompanyAccess>();

  actionTitle = "";
  actionMessage = "";
  actionWarning = "";

  adminList: User[] = [];
  userList: User[] = [];
  companyList: Company[] = [];

  adminFC = new FormControl('');
  selectedAdmin: string;
  filteredAdmin: Observable<User[]>;

  readonly maxRetryAttempts = 3;

  async ngOnInit(): Promise<void> {
    [this.userList, this.companyList] = await Promise.all([
      this.userService.getAllUsers({ roleId: [this.enums.ADMIN], isActive: true }),
      this.companyService.getAllCompanies()
    ]);

    this.companyAccessMap = new Map(this.companyAccessList.map((a) => [`${a.Username}-${a.CompanyId}`, a]));

    switch (this.action) {
      case AccessAction.Duty:
        this.adminList = this.userList.filter(u => this.adminAccessList?.filter(a => (a.companyCount + a.onDutyAccessCount + a.tempAccessCount) < this.companyList.length).map(a => a.username).includes(u.Username));
        break;
      case AccessAction.Change:
      case AccessAction.Add:
        this.adminList = this.userList.filter(u => !this.companyAccessList?.some(a => a.Username === u.Username && this.adminAccessService.isEssentialPriority(a.Priority)));
        break;
      default:
        this.adminList = this.userList.filter(u => u.Username !== this.currentAdmin);
        break;
    }

    //filter admin
    this.filteredAdmin = this.adminFC.valueChanges.pipe(
      startWith(''),
      map(username => this._filterAdminCodeOptions(username))
    );

    this.setActionMessages(this.action, this.currentAdmin);
  }

  private _filterAdminCodeOptions(value: string): User[] {
    const filterValue = value.toLowerCase();
    const filtered = this.adminList.filter(admin => { return admin.Username.toLowerCase().includes(filterValue) });

    return filtered;
  }

  setActionMessages(action: AccessAction, admin?: string) {
    switch (action) {
      case AccessAction.Add:
        this.actionTitle = "Add admin access " + this.targetPriority;
        this.actionMessage = "Add admin access to:";
        this.actionWarning = (this.selectedAdmin == undefined) ? "" : "Are you sure to add access rights of " + this.targetPriority + " to " + this.selectedAdmin;
        break;
      case AccessAction.Change:
        this.actionTitle = `Change admin access`;
        this.actionMessage = `${this.companyAccessList[0].CompanyCode}: Change ${this.targetPriority} admin access of ${admin} to:`;
        this.actionWarning = "Are you sure to change the access?";
        break;
      case AccessAction.Clone:
        this.actionTitle = "Clone access right to temporary admin";
        this.actionMessage = "Clone access rights of " + admin + " as temporary access for:";
        this.actionWarning = "";
        break;
      case AccessAction.Duty:
        this.actionTitle = "Add duty admin";
        this.actionMessage = "Add admin as on duty admin:";
        this.actionWarning = "Are you sure to add this admin as on duty admin?";
        break;
      case AccessAction.RevokeTransfer:
        this.actionTitle = "Revoke & Transfer admin access right";
        this.actionMessage = "Transfer access rights of " + admin + " to:";
        this.actionWarning = `Are you sure to revoke all access rights of ${admin}?`;
        break;
    }
  }

  getAdminAccess(username: string): AdminAccess {
    const companyAccess = this.companyAccessList
      .filter((c) => c.Username == username && c.Priority % 10 == 0)
      .map((c) => c.CompanyId);
    const tempAccess = this.companyAccessList
      .filter((c) => c.Username == username && c.Priority % 10 == 1 && c.Priority !== this.enums.ON_DUTY_PRIORITY)
      .map((c) => c.CompanyId);
    const onDutyAccess = this.companyAccessList
      .filter((c) => c.Username == username && c.Priority === this.enums.ON_DUTY_PRIORITY)
      .map((c) => c.CompanyId);
    return { username: username, companyAccess: companyAccess, companyCount: companyAccess.length, tempAccess: tempAccess, tempAccessCount: tempAccess.length, onDutyAccess: onDutyAccess, onDutyAccessCount: onDutyAccess.length };
  }

  async grantOnDutyAccess(username: string) {
    const allComp: number[] = this.companyList.map(c => c.ID);
    const currentAccess = this.adminAccessList.find(a => a.username === username);
    const compToGrant: number[] = allComp.filter(c => ![...currentAccess?.companyAccess, ...currentAccess?.tempAccess, ...currentAccess?.onDutyAccess].includes(c));
    const granted = await this.adminAccessService.grantAccessWithRetry(username, this.enums.ON_DUTY_PRIORITY, compToGrant);
    if (granted.companyIds.length === compToGrant.length) {
      this.sharedService.openAlert(`Granted on duty access to ${username} for all ${compToGrant.length} companies!`, ALERT_TYPE.SUCCESS, true);
    } else {
      this.sharedService.openAlert(`Granted on duty access to ${username} for ${granted.companyIds.length} of ${compToGrant.length} companies!`, ALERT_TYPE.DANGER, true, {auto: false});
    }
  }

  async grantEssentialAccess(username: string, priority: number, companies: number[]) {
    const tempAccess = this.companyAccessList?.some(c => c.Username === username && this.adminAccessService.getAccessType(c.Priority) !== AccessType.Normal);
    if (tempAccess) await this.adminAccessService.revokeAccess(username, companies);
    const granted = await this.adminAccessService.putGrantAccessRequest(username, priority, companies);
    if (granted.companyIds.length === companies.length) {
      this.sharedService.openAlert(`Granted ${priority} admin access to ${username}!`, ALERT_TYPE.SUCCESS, true);
    } else {
      this.sharedService.openAlert(`Failed to grant ${priority} admin access to ${username}!`, ALERT_TYPE.DANGER, true, {auto: false});
    }
  }

  checkTargetAdminAccess(targetAdmin: string, accessList: CompanyAccess[]) {
    return accessList.reduce((acc, access) => {
      const overlapped = this.companyAccessMap.get(`${targetAdmin}-${access.CompanyId}`);
      if (overlapped) {
        if (this.adminAccessService.getAccessType(overlapped.Priority) > this.adminAccessService.getAccessType(access.Priority)) {
          acc.toRevoke.push(overlapped);
          acc.toGrant.push(access);
        }
      } else {
        acc.toGrant.push(access);
      }
      return acc;
    }, { toRevoke: [] as CompanyAccess[], toGrant: [] as CompanyAccess[] });
  }

  async changeAccess(currentAdmin: string, targetAdmin: string) {
    const accessList = this.companyAccessList.filter(c => c.Username === currentAdmin);
    const { toRevoke, toGrant } = this.checkTargetAdminAccess(targetAdmin, accessList);

    await this.adminAccessService.revokeAccessWithRetry(targetAdmin, toRevoke.map(a => a.CompanyId));

    const accessMap = this.adminAccessService.deduceCompanyAccessMap(toGrant);

    const granted = await Promise.all(
      Array.from(accessMap).map(([priority, compIds]) =>
        this.adminAccessService.grantAccessWithRetry(targetAdmin, priority, compIds)
      )
    ).then(results => results.flatMap(result => result.companyIds));

    const revoked = (await this.adminAccessService.revokeAccessWithRetry(currentAdmin, granted)).companyIds;

    if (revoked.length === toGrant.length) {
      this.sharedService.openAlert(`All ${granted.length} admin access changed successfully!`, ALERT_TYPE.SUCCESS, true);
    } else {
      if (granted.length === revoked.length)
        this.sharedService.openAlert(`${granted.length} of ${toGrant.length} admin access changed successfully!`, ALERT_TYPE.DANGER, true, {auto: false});
      else if (granted.length > revoked.length)
        this.sharedService.openAlert(`${granted.length} of ${toGrant.length} admin access changed successfully! \n${granted.length - revoked.length} admin access granted to ${targetAdmin} but failed to revoke ${currentAdmin} `, ALERT_TYPE.DANGER, true, {auto: false});
    }
  }

  async cloneTempAccess(currentAdmin: string, targetAdmin: string) {
    const accessList = this.companyAccessList.reduce((acc, c) => {
      if ((c.Username === currentAdmin && c.Priority !== this.enums.ON_DUTY_PRIORITY)) {
        if (c.Priority % 10 === 0)
          c.Priority = c.Priority + 1;
        acc.push(c);
      }
      return acc;
    }, []);

    const { toRevoke, toGrant } = this.checkTargetAdminAccess(targetAdmin, accessList);
    await this.adminAccessService.revokeAccessWithRetry(targetAdmin, toRevoke.map(a => a.CompanyId));
    const accessMap = this.adminAccessService.deduceCompanyAccessMap(toGrant);
    const grantedComp = await Promise.all(
      Array.from(accessMap).map(([priority, compIds]) =>
        this.adminAccessService.grantAccessWithRetry(targetAdmin, priority, compIds)
      )
    ).then(results => results.flatMap(result => result.companyIds));

    if (grantedComp.length === toGrant.length) {
      this.sharedService.openAlert(`Granted temporary access to ${targetAdmin} for all ${toGrant.length} companies!`, ALERT_TYPE.SUCCESS, true);
    } else {
      this.sharedService.openAlert(`Granted temporary access to ${targetAdmin} for ${grantedComp.length} of ${toGrant.length} companies!`, ALERT_TYPE.DANGER, true, {auto: false});
    }
  }

  async submit() {
    switch (this.action) {
      case AccessAction.Add:
        this.grantEssentialAccess(this.selectedAdmin, this.targetPriority, this.companies);
        break;
      case AccessAction.Change:
        this.changeAccess(this.currentAdmin, this.selectedAdmin);
        break;
      case AccessAction.Clone:
        this.cloneTempAccess(this.currentAdmin, this.selectedAdmin);
        break;
      case AccessAction.Duty:
        this.grantOnDutyAccess(this.selectedAdmin);
        break;
      case AccessAction.RevokeTransfer:
        await this.changeAccess(this.currentAdmin, this.selectedAdmin);
        break;
    }
  }

  reset() {
    this.activeModal.close();
  }
}
